/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { string, array, bool, object, func } from 'prop-types';
import { Prompt } from 'react-router';
import update from 'immutability-helper';
import isEqual from 'lodash/isEqual';
import find from 'lodash/find';
import classNames from 'clsx';
import jump from 'jump.js';

import p from 'polyglot/polyglot';

import Input from './Input/Input';
import Button from 'components/Button/Button';

import styles from './form.scss';

/**
 * This is the Form component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export default class Form extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {Object}
   */
  static propTypes = {
    fieldsets: array.isRequired,
    submitText: string,
    errors: object.isRequired,
    validationMessages: object.isRequired,
    sending: bool.isRequired,
    onSubmit: func.isRequired,
    scrollToTop: bool,
    blockNavigation: bool,
    light: bool,
    wide: bool,
    autoComplete: string,
  };

  static defaultProps = {
    scrollToTop: true,
    blockNavigation: false,
    light: false,
    wide: false,
    autoComplete: 'off',
  };

  /**
   * Create Form.
   *
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    const form = {};

    props.fieldsets.forEach(({ inputs }) => {
      if (inputs) {
        inputs.forEach(input => {
          form[input.name] = {
            type: input.type,
            value: input.value,
            valid: input.required ? !!input.value : true,
          }; // || (input.type === 'image' ? [] : ''),
        });
      }
    });

    this.state = {
      focusInput: null,
      form,
      errors: props.errors,
    };

    // We save the form to compare the initial form
    // when we should blockNavigation
    // no need to keep in the state for this.
    this.cachedForm = form;
  }

  /**
   * React component lifefycle method that is triggered when the component
   * receives new props.
   *
   * @param {Object} nextProps
   */
  UNSAFE_componentWillReceiveProps({
    errors: nextErrors,
    fieldsets: nextFieldsets,
  }) {
    const { fieldsets, errors } = this.props;

    if (nextErrors) this.setErrors(nextErrors);
    if (errors && !nextErrors) this.setErrors({ message: false });

    if (!isEqual(nextFieldsets, fieldsets)) {
      nextFieldsets.forEach(({ inputs }) => {
        if (inputs) {
          inputs.forEach(input => {
            if (input.value) {
              this.validate(
                input.name,
                input.label,
                input.value,
                input.required,
                input.constraints
              );
            }
          });
        }
      });
    }
  }

  /**
   * Set error.
   *
   * @param {Object} newError
   */
  setErrors(newError) {
    const { errors } = this.state;

    Object.keys(newError).forEach(name => {
      // Iterate over errors within newErrors.
      if (!newError[name]) {
        // New error value is falsy, remove error from errors.
        delete errors[name];
      } else {
        // Set value from new error to errors.
        errors[name] = newError[name];
      }
    });

    this.setState(errors);
  }

  /**
   * Set input status.
   *
   * @param {string} name
   * @param {string|number|bool} value
   * @param {string} error
   */
  setInputStatus(name, value, error) {
    const { form, errors } = this.state;

    if (!error && errors[name]) {
      delete errors[name];
    } else {
      errors[name] = error;
    }

    this.setState({
      form: update(form, {
        [name]: {
          value: { $set: value },
          valid: { $set: !error },
        },
      }),
      errors,
    });
  }

  /**
   * Scroll to specific field.
   *
   * @param {node} field
   */
  scrollTo = field => {
    const { scrollToTop } = this.props;
    const offset = -150;
    const scroll = window.scrollY || window.pageYOffset;

    if (field) {
      jump(field, {
        offset,
        duration: distance => Math.abs(distance * 1.5),
      });
    } else if (scrollToTop) {
      jump(scroll * -1, {
        duration: distance => Math.abs(distance),
      });
    }
  };

  /**
   * Event handler that handles for submit.
   *
   * @param {Object} event
   */
  submitHandler = event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const { fieldsets, onSubmit, sending } = this.props;
    const { form } = this.state;

    if (sending) return;

    const data = {};
    let invalidField = null;
    let errorCount = 0;

    fieldsets.forEach(({ inputs }) => {
      if (inputs) {
        inputs.forEach(
          ({
            type,
            name,
            label,
            required,
            constraints,
            disabled,
            disabledWhen,
            mustMatch,
            replaceField,
          }) => {
            if (
              disabledWhen &&
              Object.keys(disabledWhen).some(key => {
                const value =
                  typeof form[key].value === 'undefined' ? '' : form[key].value;

                return typeof disabledWhen[key] === 'object'
                  ? disabledWhen[key].includes(value)
                  : disabledWhen[key] === value;
              })
            ) {
              // Skip this value if the value is disabled due to other inputs.
              return;
            }

            const valid = this.validate(
              name,
              label,
              form[name].value,
              required,
              constraints,
              mustMatch,
              replaceField
            );

            if (!valid) {
              invalidField = invalidField
                ? invalidField
                : this.form.querySelector(`label[for="${name}"]`);

              errorCount++;
            }

            if (['text', 'tags', 'textarea'].includes(type)) {
              form[name].value = form[name].value
                ? form[name].value.toString()
                : '';
            }

            if (type === 'select') {
              form[name].value = form[name].value
                ? form[name].value
                : undefined;
            }

            if (type !== 'image' && !disabled) {
              data[name] = form[name].value;
            }
          }
        );
      }
    });

    this.scrollTo(invalidField);

    if (errorCount > 0) return;

    this.cachedForm = form;

    onSubmit(data);
  };

  /**
   * Reset form values and success message.
   */
  reset = event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const { form } = this.state;

    Object.keys(form).forEach(input => {
      form[input] = {
        ...form[input],
        value: input.type === 'image' ? [] : '',
        valid: false,
      };
    });

    this.setState({
      form,
      errors: {},
    });
  };

  /**
   * Validate input according to constraints.
   *
   * @param {string} name
   * @param {string} label
   * @param {string|boolean} value
   * @param {boolean} required
   * @param {string} constraints
   * @param {string} mustMatch
   * @param {string} replaceField
   *
   * @return {boolean}
   */
  validate = (
    name,
    label,
    value,
    required,
    constraints,
    mustMatch = null,
    replaceField = null
  ) => {
    const { validationMessages } = this.props;
    const { form } = this.state;

    let err = false;
    let msg = null;
    const val = typeof value === 'string' ? value.trim() : !!value;

    if (!val && required) {
      msg = validationMessages[name]
        ? validationMessages[name].required ||
          validationMessages.required ||
          'Error message missing for [required].'
        : validationMessages.required ||
          'Error message missing for [required].';

      err = msg.replace('[label]', label || '');
    }

    // check field against constraints.
    if (val && constraints && !err) {
      msg = validationMessages[name]
        ? validationMessages[name].invalid ||
          validationMessages.invalid ||
          `Error message missing for [${name}].`
        : validationMessages.invalid || `Error message missing for [${name}].`;

      // Assign message to error only if message exists and the test returns false.
      // If test returns true or message doesn't exist, error will be false.
      // err = !pattern.test(val) && msg;
      err = !constraints.test(val)
        ? msg.replace('[label]', label || '')
        : false;
    }

    // check two fields against each other.
    if (val && mustMatch && !err) {
      const matchingValue = form[mustMatch] && form[mustMatch].value;

      msg = validationMessages[name]
        ? validationMessages[name].invalid ||
          validationMessages.invalid ||
          `Error message missing for [${name}].`
        : validationMessages.invalid || `Error message missing for [${name}].`;

      err = matchingValue !== val ? msg.replace('[label]', label || '') : false;
    }

    // replace another fields value on change if the field is empty.
    if (val && replaceField && !err) {
      const matchingField = form[replaceField];
      const isEmpty = matchingField && !matchingField.value;

      if (isEmpty) {
        process.nextTick(() => this.setInputStatus(replaceField, value, err));
      }
    }

    this.setInputStatus(name, value, err);

    return !err;
  };

  /**
   * Update state with focused input.
   *
   * @param {string} name
   */
  setFocus = name => {
    this.setState({ focusInput: name });
  };

  /**
   * Should we block the navigation if the form has been changed?
   */
  shouldBlockNavigation = () => {
    const { blockNavigation } = this.props;
    const { form } = this.state;

    if (blockNavigation) {
      return !isEqual(form, this.cachedForm);
    }

    return false;
  };

  /**
   * Render Form.
   *
   * @return {JSX}
   */
  render() {
    const {
      fieldsets,
      submitText,
      sending,
      light,
      wide,
      autoComplete,
    } = this.props;
    const { focusInput, form, errors } = this.state;

    const invalid = !!find(form, { valid: false });
    const shouldBlockNavigation = this.shouldBlockNavigation();

    return (
      <React.Fragment>
        <Prompt
          when={shouldBlockNavigation}
          message={p.t('general.confirm_leave')}
        />

        <form
          className={classNames({
            [styles.invalid]: invalid,
            [styles.sending]: sending,
          })}
          onSubmit={this.submitHandler}
          noValidate
          autoComplete={autoComplete}
          ref={ref => {
            this.form = ref;
          }}
        >
          {fieldsets.map(({ legend, inputs }, index) =>
            inputs && inputs.length > 0 ? (
              <div
                role="group"
                key={legend || index}
                className={styles.fieldset}
              >
                {legend && (
                  <div className={styles.legend}>
                    <h2>{legend}</h2>
                  </div>
                )}
                {inputs.map(input =>
                  // This is a bit hard to read, but what it does is to
                  // check if the input has a "disabledWhen" property, and
                  // if it does, and that object has a key that exists in
                  // the form and that key's value matches an input's value
                  // then it returns null instead of the input.
                  input.disabledWhen &&
                  Object.keys(input.disabledWhen).some(key => {
                    const value =
                      typeof form[key].value === 'undefined'
                        ? ''
                        : form[key].value;

                    return typeof input.disabledWhen[key] === 'object'
                      ? input.disabledWhen[key].includes(value)
                      : input.disabledWhen[key] === value;
                  }) ? null : (
                    <Input
                      key={input.name}
                      {...input}
                      value={form[input.name].value}
                      focus={focusInput === input.name}
                      error={errors[input.name] || ''}
                      validate={this.validate}
                      setFocus={this.setFocus}
                      light={light}
                    />
                  )
                )}
              </div>
            ) : null
          )}

          <footer className={styles.footer}>
            <Button
              wide={wide}
              dark={light}
              sending={sending}
              onClick={this.submitHandler}
            >
              {submitText}
            </Button>
          </footer>
        </form>
      </React.Fragment>
    );
  }
}
